import React, { useState } from "react";
import { CheckCircle2, XCircle, RotateCcw, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function PracticeTestTab({ certId }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const startTest = async () => {
    setLoading(true);
    setResult(null);
    setAnswers({});
    try {
      const { data } = await api.get(`/certifications/${certId}/practice-test?count=10`);
      setQuestions(data);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/certifications/${certId}/practice-test/grade`, { answers });
      setResult(data);
    } finally {
      setLoading(false);
    }
  };

  const resultById = result ? Object.fromEntries(result.results.map((r) => [r.id, r])) : {};

  if (!questions) {
    return (
      <div className="glass-panel rounded-2xl p-10 text-center" data-testid="practice-test-intro">
        <p className="text-slate-400 mb-5">10 untimed practice questions, picked at random from the full question bank. Get instant feedback with explanations after each attempt.</p>
        <button onClick={startTest} disabled={loading} data-testid="start-practice-test-btn" className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Practice Test"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="practice-test-panel">
      {result && (
        <div className="glass-panel rounded-2xl p-6 text-center" data-testid="practice-test-score-summary">
          <p className="font-heading text-3xl font-bold text-white">{result.score}%</p>
          <p className="text-sm text-slate-400 mt-1">{result.correct_count}/{result.total} correct</p>
        </div>
      )}
      {questions.map((q, i) => {
        const r = resultById[q.id];
        return (
          <div key={q.id} className="glass-panel rounded-2xl p-5" data-testid={`practice-question-${i}`}>
            <p className="text-sm font-medium text-white mb-3">{i + 1}. {q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => {
                const selected = answers[q.id] === oi;
                let style = "border-white/10 text-slate-300 hover:border-white/20";
                if (r) {
                  if (oi === r.correct_index) style = "border-emerald-400/50 bg-emerald-400/10 text-emerald-300";
                  else if (selected && !r.correct) style = "border-rose-400/50 bg-rose-400/10 text-rose-300";
                } else if (selected) {
                  style = "border-indigo-400/50 bg-indigo-400/10 text-indigo-300";
                }
                return (
                  <button
                    key={oi} disabled={!!result}
                    onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: oi }))}
                    data-testid={`practice-question-${i}-option-${oi}`}
                    className={`w-full text-left text-sm px-4 py-2.5 rounded-lg border transition-colors ${style}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            {r && (
              <div className={`flex items-start gap-2 mt-3 text-xs ${r.correct ? "text-emerald-400" : "text-rose-400"}`} data-testid={`practice-question-${i}-feedback`}>
                {r.correct ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                <span className="text-slate-400">{r.explanation}</span>
              </div>
            )}
          </div>
        );
      })}

      {!result ? (
        <button
          onClick={submit} disabled={loading || Object.keys(answers).length === 0}
          data-testid="submit-practice-test-btn"
          className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : `Submit (${Object.keys(answers).length}/${questions.length} answered)`}
        </button>
      ) : (
        <button onClick={startTest} data-testid="retry-practice-test-btn" className="w-full flex items-center justify-center gap-2 border border-white/10 hover:border-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
          <RotateCcw className="w-4 h-4" /> Try 10 New Questions
        </button>
      )}
    </div>
  );
}
